import { useTranslation } from '@rhino-project/core/i18n';
import { useEffect, useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  FieldGroup,
  Fieldset,
  Input,
  Label,
  TextField,
  toast,
  Typography
} from '@heroui/react';
import {
  useModel,
  useModelShow,
  useModelUpdate
} from '@rhino-project/core/hooks';
import { networkApiCallOnlyData } from '@rhino-project/core/lib';
import {
  CalendarIcsPreview,
  type CalendarPreviewEvent
} from '../../../../../components/calendar/CalendarIcsPreview';

export const AdminCalendar = () => {
  const { t } = useTranslation();

  const { team_season_id } = useParams({ strict: false }) as {
    team_season_id: string;
  };
  const teamSeasonId = Number(team_season_id);
  const { resource } = useModelShow('team_season', teamSeasonId);
  const teamSeason = resource;

  const { mutate: updateTeamSeason, isPending } = useModelUpdate('team_season');

  const [embedUrl, setEmbedUrl] = useState('');
  const [icsUrl, setIcsUrl] = useState('');
  const [dirty, setDirty] = useState(false);

  const teamSeasonModel = useModel('team_season');
  const [debouncedIcs, setDebouncedIcs] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedIcs(icsUrl.trim()), 500);
    return () => clearTimeout(timer);
  }, [icsUrl]);

  const { data: previewData, isFetching: previewLoading } = useQuery({
    queryKey: [
      'calendarPreview',
      teamSeasonModel.path,
      teamSeasonId,
      debouncedIcs
    ],
    queryFn: () =>
      networkApiCallOnlyData(
        `${teamSeasonModel.path}/${teamSeasonId}/calendar_preview`,
        { method: 'post', data: { ics_url: debouncedIcs } }
      ) as Promise<{
        events: CalendarPreviewEvent[];
        count: number;
        fetch_error?: string;
      }>,
    enabled: !!debouncedIcs
  });

  useEffect(() => {
    if (!teamSeason) return;
    // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- Preserve existing behavior while enabling the rule globally.
    setEmbedUrl((teamSeason.calendar_embed_url as string | null) ?? '');
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- Preserve existing behavior while enabling the rule globally.
    setIcsUrl((teamSeason.calendar_ics_url as string | null) ?? '');
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- Preserve existing behavior while enabling the rule globally.
    setDirty(false);
  }, [teamSeason]);

  const handleIcsChange = (value: string) => {
    setIcsUrl(value);
    setDirty(true);
  };

  const handleEmbedChange = (value: string) => {
    setEmbedUrl(value);
    setDirty(true);
  };

  const handleSave = () => {
    updateTeamSeason(
      {
        id: teamSeasonId,
        calendar_embed_url: embedUrl || null,
        calendar_ics_url: icsUrl || null
      } as never,
      {
        onSuccess: () => {
          setDirty(false);
          toast.success(t('frontend.messages.calendar_settings_saved'));
        }
      }
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Typography.Heading level={1}>
          {t('frontend.messages.team_calendar')}
        </Typography.Heading>
        <Button
          variant="primary"
          onPress={handleSave}
          isDisabled={isPending || !dirty}
        >
          {t('frontend.messages.save')}
        </Button>
      </div>

      <div className="flex flex-col gap-6">
        <Card className="p-6">
          <Card.Header>
            <Card.Title>{t('frontend.messages.calendar_source')}</Card.Title>
            <Card.Description>
              {t(
                'frontend.messages.configure_how_the_portal_renders_this_team_s_calendar_you_can'
              )}
            </Card.Description>
          </Card.Header>
          <Card.Content>
            <Fieldset>
              <FieldGroup>
                <TextField
                  variant="secondary"
                  value={icsUrl}
                  onChange={handleIcsChange}
                >
                  <Label>{t('frontend.messages.ics_feed_url')}</Label>
                  <Input placeholder="https://example.com/calendar.ics" />
                </TextField>

                <TextField
                  variant="secondary"
                  value={embedUrl}
                  onChange={handleEmbedChange}
                >
                  <Label>
                    {t('frontend.messages.google_calendar_embed_url')}
                  </Label>
                  <Input placeholder="https://calendar.google.com/calendar/embed?src=..." />
                </TextField>
              </FieldGroup>
            </Fieldset>
            {debouncedIcs && (
              <div className="mt-4">
                <Typography.Heading level={3} className="text-sm mb-2">
                  {t('frontend.messages.feed_preview')}
                </Typography.Heading>
                <CalendarIcsPreview
                  isLoading={previewLoading}
                  count={previewData?.count}
                  events={previewData?.events}
                  fetchError={previewData?.fetch_error}
                />
              </div>
            )}
          </Card.Content>
        </Card>

        {!icsUrl && embedUrl && (
          <Card className="overflow-hidden">
            <iframe
              className="border-none w-full"
              height="600"
              src={embedUrl}
              title={t('frontend.messages.team_calendar_preview')}
            />
          </Card>
        )}
      </div>
    </div>
  );
};

export const Route = createFileRoute(
  '/_authenticated/$owner/active/$team_season_id/calendar'
)({
  component: AdminCalendar
});
